/**
 * MediaComponents - Media handling utilities for CreateTemplateWizard
 * Converted from TypeScript for PromptusAI PWA
 */

import React, { useState, useEffect } from 'react';

// ============================================
// Magic Number Detection (from binary data)
// ============================================

const base64ToBytes = (base64String) => {
  try {
    const cleaned = base64String.trim().replace(/\s/g, '');
    const padLen = (4 - (cleaned.length % 4)) % 4;
    const padded = cleaned + '='.repeat(padLen);
    const binaryStr = atob(padded);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes;
  } catch (err) {
    console.error('Error decoding base64:', err);
    return new Uint8Array(0);
  }
};

const extractBase64Data = (input) => {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (trimmed.startsWith('data:')) {
    const commaIndex = trimmed.indexOf(',');
    if (commaIndex !== -1) {
      return trimmed.slice(commaIndex + 1);
    }
    return null;
  }
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('blob:')) {
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    if (trimmed.length >= 16 && base64Regex.test(trimmed.replace(/\s/g, ''))) {
      return trimmed;
    }
  }
  return null;
};

const toHex = (bytes, start, end) => {
  let out = '';
  for (let i = start; i < Math.min(end, bytes.length); i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
};

const strEq = (bytes, offset, str) => {
  if (offset + str.length > bytes.length) return false;
  for (let i = 0; i < str.length; i++) {
    if (bytes[offset + i] !== str.charCodeAt(i)) return false;
  }
  return true;
};

const detectFromBytes = (bytes) => {
  if (bytes.length < 12) return null;
  const hex = toHex(bytes, 0, Math.min(bytes.length, 64));

  if (hex.startsWith('89504e470d0a1a0a')) return { mime: 'image/png', ext: 'png', type: 'image' };
  if (hex.startsWith('ffd8ff')) return { mime: 'image/jpeg', ext: 'jpg', type: 'image' };
  if (hex.startsWith('474946383761') || hex.startsWith('474946383961')) return { mime: 'image/gif', ext: 'gif', type: 'image' };
  if (hex.startsWith('1a45dfa3')) return { mime: 'video/webm', ext: 'webm', type: 'video' };
  if (strEq(bytes, 0, 'ID3') || (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0)) return { mime: 'audio/mpeg', ext: 'mp3', type: 'audio' };
  if (strEq(bytes, 0, 'OggS')) return { mime: 'audio/ogg', ext: 'ogg', type: 'audio' };
  if (strEq(bytes, 0, 'fLaC')) return { mime: 'audio/flac', ext: 'flac', type: 'audio' };

  if (bytes.length >= 12 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    if (brand === 'M4A ' || brand === 'M4B ' || brand === 'M4P ') return { mime: 'audio/mp4', ext: 'm4a', type: 'audio' };
    return { mime: 'video/mp4', ext: 'mp4', type: 'video' };
  }

  if (strEq(bytes, 0, 'RIFF') && bytes.length >= 12) {
    if (strEq(bytes, 8, 'WEBP')) return { mime: 'image/webp', ext: 'webp', type: 'image' };
    if (strEq(bytes, 8, 'WAVE')) return { mime: 'audio/wav', ext: 'wav', type: 'audio' };
    if (strEq(bytes, 8, 'AVI ')) return { mime: 'video/avi', ext: 'avi', type: 'video' };
  }

  if (strEq(bytes, 0, 'glTF')) return { mime: 'model/gltf-binary', ext: 'glb', type: '3d' };
  if (strEq(bytes, 0, 'BM')) return { mime: 'image/bmp', ext: 'bmp', type: 'image' };
  if ((bytes[0] === 0x49 && bytes[1] === 0x49) || (bytes[0] === 0x4D && bytes[1] === 0x4D)) return { mime: 'image/tiff', ext: 'tiff', type: 'image' };

  return null;
};

export const detectMediaTypeAsync = async (input) => {
  if (!input) return 'image';
  try {
    const base64Data = extractBase64Data(input);
    if (base64Data) {
      const bytes = base64ToBytes(base64Data);
      if (bytes.length > 0) {
        const detected = detectFromBytes(bytes);
        if (detected) return detected.type;
      }
    }
    if (input.startsWith('http://') || input.startsWith('https://')) {
      try {
        const response = await fetch(input, { method: 'GET', headers: { Range: 'bytes=0-63' } });
        if (response.ok) {
          const buffer = await response.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          const detected = detectFromBytes(bytes);
          if (detected) return detected.type;
        }
      } catch (fetchError) {
        console.warn('[MediaType] Could not fetch URL for detection:', fetchError);
      }
    }
    return detectMediaTypeFromUrl(input);
  } catch (error) {
    console.error('[MediaType] Detection error:', error);
    return detectMediaTypeFromUrl(input);
  }
};

export const detectMediaTypeFromUrl = (url) => {
  if (!url) return 'image';
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.startsWith('data:')) {
    if (lowerUrl.startsWith('data:video/')) return 'video';
    if (lowerUrl.startsWith('data:audio/')) return 'audio';
    if (lowerUrl.startsWith('data:model/')) return '3d';
    if (lowerUrl.startsWith('data:image/')) return 'image';
  }

  if (lowerUrl.match(/\.(mp4|webm|mov|avi|mkv|m4v|wmv|flv)(\?|$)/i)) return 'video';
  if (lowerUrl.match(/\.(mp3|wav|ogg|flac|m4a|aac|wma|opus)(\?|$)/i)) return 'audio';
  if (lowerUrl.match(/\.(glb|gltf|obj|fbx|stl|3ds|dae|ply)(\?|$)/i)) return '3d';

  return 'image';
};

export const detectMediaType = (url) => {
  if (!url) return 'image';
  const base64Data = extractBase64Data(url);
  if (base64Data) {
    const bytes = base64ToBytes(base64Data);
    if (bytes.length > 0) {
      const detected = detectFromBytes(bytes);
      if (detected) return detected.type;
    }
  }
  return detectMediaTypeFromUrl(url);
};

export const getOutputTypeFromCategory = (category) => {
  if (!category) return 'image';
  const lowerCat = category.toLowerCase();
  if (lowerCat.includes('video') || lowerCat.includes('animation')) return 'video';
  if (lowerCat.includes('audio') || lowerCat.includes('music') || lowerCat.includes('sound')) return 'audio';
  if (lowerCat.includes('3d') || lowerCat.includes('mesh') || lowerCat.includes('model')) return '3d';
  return 'image';
};

export const mediaBackgroundColors = {
  video: 'bg-gradient-to-br from-purple-900/80 to-purple-800/60',
  audio: 'bg-gradient-to-br from-green-900/80 to-green-800/60',
  '3d': 'bg-gradient-to-br from-orange-900/80 to-orange-800/60',
};

// ============================================
// Media Type Badge Component
// ============================================

const mediaTypeBadgeConfig = {
  image: {
    icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    color: 'bg-blue-500/20 text-blue-400',
    label: 'Image',
  },
  video: {
    icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
    color: 'bg-purple-500/20 text-purple-400',
    label: 'Video',
  },
  audio: {
    icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" /></svg>,
    color: 'bg-green-500/20 text-green-400',
    label: 'Audio',
  },
  '3d': {
    icon: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>,
    color: 'bg-orange-500/20 text-orange-400',
    label: '3D',
  },
};

export function MediaTypeBadge({ type, className = '' }) {
  const config = mediaTypeBadgeConfig[type] || mediaTypeBadgeConfig.image;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color} ${className}`}>
      {config.icon}
      {config.label}
    </span>
  );
}

// ============================================
// Media Renderer Component
// ============================================

export function MediaRenderer({ url, alt, className = '', showControls = false, mediaType: providedMediaType }) {
  const [detectedType, setDetectedType] = useState(null);
  const mediaType = providedMediaType || detectedType || detectMediaType(url);

  useEffect(() => {
    if (providedMediaType || detectedType) return;
    let cancelled = false;
    const detectType = async () => {
      try {
        const type = await detectMediaTypeAsync(url);
        if (!cancelled) setDetectedType(type);
      } catch (error) {
        console.error('[MediaRenderer] Error detecting media type:', error);
      }
    };
    detectType();
    return () => { cancelled = true; };
  }, [url, providedMediaType, detectedType]);

  useEffect(() => { setDetectedType(null); }, [url]);

  if (mediaType === 'video') {
    return (
      <div className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
        <video src={url} className="w-full h-full object-cover" controls={showControls} muted loop playsInline
          onMouseEnter={(e) => !showControls && e.target.play()}
          onMouseLeave={(e) => !showControls && e.target.pause()}
        />
        {!showControls && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full bg-purple-500/70 flex items-center justify-center">
              <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (mediaType === 'audio') {
    return (
      <div className={`relative ${className} ${mediaBackgroundColors.audio} flex flex-col items-center justify-center p-2`} onClick={(e) => e.stopPropagation()}>
        <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center mb-2">
          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" /></svg>
        </div>
        <span className="text-green-300 text-xs font-medium mb-2">Audio</span>
        <audio src={url} controls className="w-full max-w-full h-8" style={{ maxWidth: '100%' }} />
      </div>
    );
  }

  if (mediaType === '3d') {
    return (
      <div className={`relative ${className} ${mediaBackgroundColors['3d']} flex flex-col items-center justify-center`}>
        <div className="w-12 h-12 rounded-full bg-orange-500/30 flex items-center justify-center mb-2">
          <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>
        </div>
        <span className="text-orange-300 text-xs font-medium">3D Model</span>
      </div>
    );
  }

  return <img src={url} alt={alt} className={`w-full h-full object-cover ${className}`} loading="lazy" />;
}

// ============================================
// Spinner Components
// ============================================

export function DefaultLoadingSpinner({ className = '' }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="w-8 h-8 border-3 border-white/20 border-t-yellow rounded-full animate-spin" />
    </div>
  );
}

export function DefaultButtonSpinner() {
  return (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

// ============================================
// Icons
// ============================================

export const Icons = {
  Close: () => <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
  Check: () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>,
  ChevronLeft: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>,
  ChevronRight: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
  ChevronDown: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
  Plus: () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
  Image: () => <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  Upload: () => <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>,
};
